import { ruPlural } from '@/lib/content/declension';
import { ruCountryFor } from '@/i18n/countryCases';

/**
 * UI-тексты инструментов eSIM: проверка устройства (ESimDeviceCheckPage),
 * подбор тарифа (ESimQuizPage), результаты подбора (QuizResults) и
 * промокоды eSIM (ESimPromotionsContent).
 *
 * Только вертикаль/компонент-специфика. Общие надзаголовки/якоря/теги — в servicePage.
 * Интерполируемые строки — функции; склонения/формат чисел остаются в компонентах
 * (страна/город приходят сюда уже готовыми, число для плюрализации — аргументом).
 *
 * RU и EN имеют идентичную форму ключей.
 */
export const esimToolsUi = {
    en: {
        // ── Device-check page ──────────────────────────────
        deviceCheck: {
            back: '← eSIM for your trip',
            kick: 'eSIM · device check',
            title: 'Does your phone support eSIM?',
            sub: 'Find your model in the list or start typing. We will show whether eSIM works and how to check manually.',
            searchPlaceholder: 'e.g. iPhone 13, Galaxy S23, Pixel 7…',
            searchAria: 'Search model',
            searchHint: '// type a model or pick one from the list below',
            supports: 'Supports eSIM',
            notSupported: 'eSIM not supported',
            choosePlan: 'Choose a plan →',
            alternatives: 'Alternatives →',
            popularModels: 'Popular models',
            tapToCheck: 'tap to check',
            empty: 'Model not found. Check the spelling or use the manual check below.',
            manualCheck: 'Manual check',
            twoWays: '2 ways',
            manualTitle: 'If your model is not listed',
            manualLead: 'Check eSIM support in your phone settings or by using the device EID.',
            faqEyebrow: 'Questions & answers',
            faqTitle: 'Common compatibility questions',
            faqLead: 'Answers on what affects eSIM support and what to do if your model is not listed.',
            models: {},
            // Brand labels for the curated device list (keyed by stable id or device `n`)
            brand: {
                'iPhone 17 / Air / Pro / Pro Max': 'Apple · 2025',
                'iPhone 16 / Plus / Pro / 16e': 'Apple · 2024',
                'iPhone 15 / Pro': 'Apple · 2023',
                'iPhone 14 / Pro': 'Apple · 2022',
                'iPhone 13 / Pro': 'Apple · 2021',
                'iPhone 12 / Pro': 'Apple · 2020',
                'iPhone 11 / XS / XR': 'Apple · 2018–19',
                'iPhone SE (2020+)': 'Apple · 2020',
                'iphone-8-older': 'Apple · pre-2017',
                'Galaxy S24 / S23 / S22': 'Samsung · 2022+',
                'Galaxy S21 / S20': 'Samsung · 2020–21',
                'Galaxy Z Fold / Flip': 'Samsung',
                'Galaxy A55 / A54': 'Samsung · mid-range',
                'Galaxy A12 / A03': 'Samsung · budget',
                'Pixel 8 / 7 / 6': 'Google · 2021+',
                'Pixel 4 / 5': 'Google · 2019–20',
                'Pixel 3 / 3a': 'Google · 2018–19',
                'Xiaomi 14 / 13': 'Xiaomi · flagship',
                'xiaomi-redmi-most': 'Xiaomi · budget',
                'huawei-p40-mate-no-gms': 'Huawei',
            },
            // Manual-check steps
            steps: [
                { n: 'iOS', title: 'Dial *#06#', d: 'If an EID line appears, the phone supports eSIM.' },
                { n: 'iOS', title: 'Settings → Cellular', d: 'An "Add eSIM" option means it is supported.' },
                { n: 'Android', title: 'Settings → Connections', d: 'SIM manager → "Add eSIM". If present, it is supported.' },
                { n: 'Android', title: 'Code *#06#', d: 'An EID number on screen confirms support.' },
            ],
        },

        // ── Quiz page ──────────────────────────────────────
        quiz: {
            kickShort: 'eSIM · finder',
            back: '← eSIM for your trip',
            kick: 'eSIM · plan finder',
            errorLabel: 'Error',
            errorLoadPlans: 'Could not load plans',
            loading: 'Loading...',
            refresh: 'Refresh →',
            questionWord: 'Question',
            questionPrefix: 'Question ',
        },

        // ── Quiz results ───────────────────────────────────
        results: {
            kick: 'Results',
            coverageLocal: 'Local',
            coverageGlobal: 'Global',
            coverageRegional: 'Regional',
            showing: (n) => `Showing ${n} plans by relevance`,
            found: (n) => `Found ${n} matching plans`,
            coverageCol: 'Coverage',
            perGbCol: 'Per GB',
            payAsYouGo: 'Pay-as-you-go',
            unlimited: 'Unlimited',
            gb: 'GB',
            calls: 'calls',
            perGbSuffix: ' / GB',
            select: 'Select →',
            details: 'Details →',
            backToQuestions: 'Back to questions',
        },

        // ── Promotions subpage ─────────────────────────────
        promos: {
            back: (cName) => `← eSIM${cName ? ` for ${cName}` : ''}`,
            kick: (cName) => `eSIM${cName ? ` · ${cName}` : ''} · discounts`,
            title: 'eSIM promo codes & deals',
            countLabel: (n) => `${n} ${n === 1 ? 'promo code' : 'promo codes'}`,
            categoriesAria: 'Promo categories',
            all: 'All',
            secmark: 'Promo codes',
            current: 'current',
            empty: 'No promo codes in this category.',
            ctaEyebrow: 'Discount applies at checkout',
            ctaTitle: 'Pick an eSIM plan and enter the promo code on the provider page.',
            ctaBtn: 'See plans →',
            disclaimer: '// Promo codes may change or expire without notice. Confirm the discount and terms on the provider page.',
        },
    },
    ru: {
        // ── Device-check page ──────────────────────────────
        deviceCheck: {
            back: '← eSIM для поездки',
            kick: 'eSIM · проверка устройства',
            title: 'Поддерживает ли ваш телефон eSIM?',
            sub: 'Найдите свою модель в списке или начните вводить название. Покажем, можно ли установить eSIM, и подскажем, как проверить вручную.',
            searchPlaceholder: 'Например: iPhone 13, Galaxy S23, Pixel 7…',
            searchAria: 'Поиск модели',
            searchHint: '// введите модель или выберите из списка ниже',
            supports: 'Поддерживает eSIM',
            notSupported: 'eSIM не поддерживается',
            choosePlan: 'Выбрать тариф →',
            alternatives: 'Альтернативы →',
            popularModels: 'Популярные модели',
            tapToCheck: 'нажмите, чтобы проверить',
            empty: 'Модель не найдена. Проверьте написание или используйте ручную проверку ниже.',
            manualCheck: 'Проверка вручную',
            twoWays: '2 способа',
            manualTitle: 'Если модели нет в списке',
            manualLead: 'Проверьте поддержку eSIM через настройки телефона или по EID устройства.',
            faqEyebrow: 'Вопросы и ответы',
            faqTitle: 'Частые вопросы о совместимости',
            faqLead: 'Поясняем, от чего зависит поддержка eSIM и что делать, если модели нет в списке.',
            models: {
                'iphone-8-older': 'iPhone 8 и старше',
                'xiaomi-redmi-most': 'Xiaomi Redmi (большинство моделей)',
                'huawei-p40-mate-no-gms': 'Huawei P40 / Mate (без GMS)',
            },
            brand: {
                'iPhone 17 / Air / Pro / Pro Max': 'Apple · 2025',
                'iPhone 16 / Plus / Pro / 16e': 'Apple · 2024',
                'iPhone 15 / Pro': 'Apple · 2023',
                'iPhone 14 / Pro': 'Apple · 2022',
                'iPhone 13 / Pro': 'Apple · 2021',
                'iPhone 12 / Pro': 'Apple · 2020',
                'iPhone 11 / XS / XR': 'Apple · 2018–19',
                'iPhone SE (2020+)': 'Apple · 2020',
                'iphone-8-older': 'Apple · до 2017',
                'Galaxy S24 / S23 / S22': 'Samsung · 2022+',
                'Galaxy S21 / S20': 'Samsung · 2020–21',
                'Galaxy Z Fold / Flip': 'Samsung',
                'Galaxy A55 / A54': 'Samsung · средний класс',
                'Galaxy A12 / A03': 'Samsung · бюджет',
                'Pixel 8 / 7 / 6': 'Google · 2021+',
                'Pixel 4 / 5': 'Google · 2019–20',
                'Pixel 3 / 3a': 'Google · 2018–19',
                'Xiaomi 14 / 13': 'Xiaomi · флагман',
                'xiaomi-redmi-most': 'Xiaomi · бюджет',
                'huawei-p40-mate-no-gms': 'Huawei',
            },
            steps: [
                { n: 'iOS', title: 'Наберите *#06#', d: 'Если в списке есть строка EID — телефон поддерживает eSIM.' },
                { n: 'iOS', title: 'Настройки → Сотовая связь', d: 'Пункт «Добавить eSIM» означает поддержку.' },
                { n: 'Android', title: 'Настройки → Подключения', d: 'SIM-менеджер → «Добавить eSIM». Наличие пункта = поддержка.' },
                { n: 'Android', title: 'Код *#06#', d: 'Наличие номера EID на экране подтверждает поддержку.' },
            ],
        },

        // ── Quiz page ──────────────────────────────────────
        quiz: {
            kickShort: 'eSIM · подбор',
            back: '← eSIM для поездки',
            kick: 'eSIM · подбор тарифа',
            errorLabel: 'Ошибка',
            errorLoadPlans: 'Не удалось загрузить тарифы',
            loading: 'Загрузка...',
            refresh: 'Обновить →',
            questionWord: 'Вопрос',
            questionPrefix: 'Вопрос ',
        },

        // ── Quiz results ───────────────────────────────────
        results: {
            kick: 'Результаты',
            coverageLocal: 'Локально',
            coverageGlobal: 'Глобально',
            coverageRegional: 'Регион',
            showing: (n) => `Показано ${n} ${ruPlural(n, ['тарифа', 'тарифов', 'тарифов'])} по релевантности`,
            found: (n) => `Найдено ${n} ${ruPlural(n, ['подходящий тариф', 'подходящих тарифа', 'подходящих тарифов'])}`,
            coverageCol: 'Покрытие',
            perGbCol: 'За ГБ',
            payAsYouGo: 'По факту',
            unlimited: 'Безлимит',
            gb: 'ГБ',
            calls: 'звонки',
            perGbSuffix: ' / ГБ',
            select: 'Выбрать →',
            details: 'Подробнее →',
            backToQuestions: 'Назад к вопросам',
        },

        // ── Promotions subpage ─────────────────────────────
        promos: {
            back: (cName) => `← eSIM${cName ? ` ${ruCountryFor(cName)}` : ''}`,
            kick: (cName) => `eSIM${cName ? ` · ${cName}` : ''} · скидки`,
            title: 'Промокоды и акции на eSIM',
            countLabel: (n) => `${n} ${ruPlural(n, ['промокод', 'промокода', 'промокодов'])}`,
            categoriesAria: 'Категории промокодов',
            all: 'Все',
            secmark: 'Промокоды',
            current: 'актуально',
            empty: 'Нет промокодов в этой категории.',
            ctaEyebrow: 'Скидка применяется при оформлении',
            ctaTitle: 'Выберите тариф eSIM и введите промокод на странице провайдера.',
            ctaBtn: 'Смотреть тарифы →',
            disclaimer: '// Промокоды могут меняться или заканчиваться без предупреждения. Размер скидки и условия уточняйте на странице провайдера.',
        },
    },
};
